import { objectSpread2 as _objectSpread2, slicedToArray as _slicedToArray, toConsumableArray as _toConsumableArray, createClass as _createClass, classCallCheck as _classCallCheck, typeof as _typeof } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import { Fragment, Slice, Node as Node$1, Schema, DOMParser } from '../../../prosemirror-model/dist/index.js';
import { liftTarget, joinPoint, canSplit, ReplaceStep, ReplaceAroundStep, canJoin } from '../../../prosemirror-transform/dist/index.js';
import { Plugin, PluginKey, Selection, TextSelection, AllSelection, NodeSelection } from '../../../prosemirror-state/dist/index.js';
import { createParagraphNear as createParagraphNear$1, deleteSelection as deleteSelection$1, exitCode as exitCode$1, joinUp as joinUp$1, joinDown as joinDown$1, joinBackward as joinBackward$1, joinForward as joinForward$1, joinTextblockBackward as joinTextblockBackward$1, joinTextblockForward as joinTextblockForward$1, lift as lift$1, liftEmptyBlock as liftEmptyBlock$1, newlineInCode as newlineInCode$1, selectNodeBackward as selectNodeBackward$1, selectNodeForward as selectNodeForward$1, selectParentNode as selectParentNode$1, selectTextblockEnd as selectTextblockEnd$1, selectTextblockStart as selectTextblockStart$1, setBlockType, wrapIn as wrapIn$1 } from '../../../prosemirror-commands/dist/index.js';
import { liftListItem as liftListItem$1, sinkListItem as sinkListItem$1, wrapInList as wrapInList$1 } from '../../../prosemirror-schema-list/dist/index.js';

/**
 * Takes a Transaction & Editor State and turns it into a chainable state object
 * @param config The transaction and state to create the chainable state from
 * @returns A chainable Editor state object
 */
function createChainableState(config) {
  var state = config.state,
    transaction = config.transaction;
  var selection = transaction.selection;
  var doc = transaction.doc;
  var storedMarks = transaction.storedMarks;
  return _objectSpread2(_objectSpread2({}, state), {}, {
    apply: state.apply.bind(state),
    applyTransaction: state.applyTransaction.bind(state),
    plugins: state.plugins,
    schema: state.schema,
    reconfigure: state.reconfigure.bind(state),
    toJSON: state.toJSON.bind(state),
    get storedMarks() {
      return storedMarks;
    },
    get selection() {
      return selection;
    },
    get doc() {
      return doc;
    },
    get tr() {
      selection = transaction.selection;
      doc = transaction.doc;
      storedMarks = transaction.storedMarks;
      return transaction;
    }
  });
}
var CommandManager = /*#__PURE__*/function () {
  function CommandManager(props) {
    _classCallCheck(this, CommandManager);
    this.editor = props.editor;
    this.rawCommands = this.editor.extensionManager.commands;
    this.customState = props.state;
  }
  return _createClass(CommandManager, [{
    key: "hasCustomState",
    get: function get() {
      return !!this.customState;
    }
  }, {
    key: "state",
    get: function get() {
      return this.customState || this.editor.state;
    }
  }, {
    key: "commands",
    get: function get() {
      var _this = this;
      var rawCommands = this.rawCommands,
        editor = this.editor,
        state = this.state;
      var view = editor.view;
      var tr = state.tr;
      var props = this.buildProps(tr);
      return Object.fromEntries(Object.entries(rawCommands).map(function (_ref) {
        var _ref2 = _slicedToArray(_ref, 2),
          name = _ref2[0],
          command = _ref2[1];
        var method = function method() {
          var callback = command.apply(void 0, arguments)(props);
          if (!tr.getMeta('preventDispatch') && !_this.hasCustomState) {
            view.dispatch(tr);
          }
          return callback;
        };
        return [name, method];
      }));
    }
  }, {
    key: "chain",
    get: function get() {
      var _this2 = this;
      return function () {
        return _this2.createChain();
      };
    }
  }, {
    key: "can",
    get: function get() {
      var _this3 = this;
      return function () {
        return _this3.createCan();
      };
    }
  }, {
    key: "createChain",
    value: function createChain(startTr) {
      var _this4 = this;
      var shouldDispatch = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      var rawCommands = this.rawCommands,
        editor = this.editor,
        state = this.state;
      var view = editor.view;
      var callbacks = [];
      var hasStartTransaction = !!startTr;
      var tr = startTr || state.tr;
      var run = function run() {
        if (!hasStartTransaction && shouldDispatch && !tr.getMeta('preventDispatch') && !_this4.hasCustomState) {
          view.dispatch(tr);
        }
        return callbacks.every(function (callback) {
          return callback === true;
        });
      };
      var chain = _objectSpread2(_objectSpread2({}, Object.fromEntries(Object.entries(rawCommands).map(function (_ref3) {
        var _ref4 = _slicedToArray(_ref3, 2),
          name = _ref4[0],
          command = _ref4[1];
        var chainedCommand = function chainedCommand() {
          var props = _this4.buildProps(tr, shouldDispatch);
          var callback = command.apply(void 0, arguments)(props);
          callbacks.push(callback);
          return chain;
        };
        return [name, chainedCommand];
      }))), {}, {
        run: run
      });
      return chain;
    }
  }, {
    key: "createCan",
    value: function createCan(startTr) {
      var _this5 = this;
      var rawCommands = this.rawCommands,
        state = this.state;
      var dispatch = false;
      var tr = startTr || state.tr;
      var props = this.buildProps(tr, dispatch);
      var formattedCommands = Object.fromEntries(Object.entries(rawCommands).map(function (_ref5) {
        var _ref6 = _slicedToArray(_ref5, 2),
          name = _ref6[0],
          command = _ref6[1];
        return [name, function () {
          return command.apply(void 0, arguments)(_objectSpread2(_objectSpread2({}, props), {}, {
            dispatch: undefined
          }));
        }];
      }));
      return _objectSpread2(_objectSpread2({}, formattedCommands), {}, {
        chain: function chain() {
          return _this5.createChain(tr, dispatch);
        }
      });
    }
  }, {
    key: "buildProps",
    value: function buildProps(tr) {
      var _this6 = this;
      var shouldDispatch = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      var rawCommands = this.rawCommands,
        editor = this.editor,
        state = this.state;
      var view = editor.view;
      var props = {
        tr: tr,
        editor: editor,
        view: view,
        state: createChainableState({
          state: state,
          transaction: tr
        }),
        dispatch: shouldDispatch ? function () {
          return undefined;
        } : undefined,
        chain: function chain() {
          return _this6.createChain(tr, shouldDispatch);
        },
        can: function can() {
          return _this6.createCan(tr);
        },
        get commands() {
          return Object.fromEntries(Object.entries(rawCommands).map(function (_ref7) {
            var _ref8 = _slicedToArray(_ref7, 2),
              name = _ref8[0],
              command = _ref8[1];
            return [name, function () {
              return command.apply(void 0, arguments)(props);
            }];
          }));
        }
      };
      return props;
    }
  }]);
}();
/**
 * Returns a field from an extension
 * @param extension The Tiptap extension
 * @param field The field, for example `renderHTML` or `priority`
 * @param context The context object that should be passed as `this` into the function
 * @returns The field value
 */
function getExtensionField(extension, field, context) {
  if (extension.config[field] === undefined && extension.parent) {
    return getExtensionField(extension.parent, field, context);
  }
  if (typeof extension.config[field] === 'function') {
    var value = extension.config[field].bind(_objectSpread2(_objectSpread2({}, context), {}, {
      parent: extension.parent ? getExtensionField(extension.parent, field, context) : null
    }));
    return value;
  }
  return extension.config[field];
}
function splitExtensions(extensions) {
  var baseExtensions = extensions.filter(function (extension) {
    return extension.type === 'extension';
  });
  var nodeExtensions = extensions.filter(function (extension) {
    return extension.type === 'node';
  });
  var markExtensions = extensions.filter(function (extension) {
    return extension.type === 'mark';
  });
  return {
    baseExtensions: baseExtensions,
    nodeExtensions: nodeExtensions,
    markExtensions: markExtensions
  };
}
function getNodeType(nameOrType, schema) {
  if (typeof nameOrType === 'string') {
    if (!schema.nodes[nameOrType]) {
      throw Error("There is no node type named '".concat(nameOrType, "'. Maybe you forgot to add the extension?"));
    }
    return schema.nodes[nameOrType];
  }
  return nameOrType;
}
function mergeAttributes() {
  for (var _len3 = arguments.length, objects = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
    objects[_key3] = arguments[_key3];
  }
  return objects.filter(function (item) {
    return !!item;
  }).reduce(function (items, item) {
    var mergedAttributes = _objectSpread2({}, items);
    Object.entries(item).forEach(function (_ref13) {
      var _ref14 = _slicedToArray(_ref13, 2),
        key = _ref14[0],
        value = _ref14[1];
      var exists = mergedAttributes[key];
      if (!exists) {
        mergedAttributes[key] = value;
        return;
      }
      if (key === 'class') {
        var valueClasses = value ? String(value).split(' ') : [];
        var existingClasses = mergedAttributes[key] ? mergedAttributes[key].split(' ') : [];
        var insertClasses = valueClasses.filter(function (valueClass) {
          return !existingClasses.includes(valueClass);
        });
        mergedAttributes[key] = [].concat(_toConsumableArray(existingClasses), _toConsumableArray(insertClasses)).join(' ');
      } else if (key === 'style') {
        var newStyles = value ? value.split(';').map(function (style) {
          return style.trim();
        }).filter(Boolean) : [];
        var existingStyles = mergedAttributes[key] ? mergedAttributes[key].split(';').map(function (style) {
          return style.trim();
        }).filter(Boolean) : [];
        var styleMap = new Map();
        existingStyles.forEach(function (style) {
          var _style$split$map = style.split(':').map(function (part) {
              return part.trim();
            }),
            _style$split$map2 = _slicedToArray(_style$split$map, 2),
            property = _style$split$map2[0],
            val = _style$split$map2[1];
          styleMap.set(property, val);
        });
        newStyles.forEach(function (style) {
          var _style$split$map3 = style.split(':').map(function (part) {
              return part.trim();
            }),
            _style$split$map4 = _slicedToArray(_style$split$map3, 2),
            property = _style$split$map4[0],
            val = _style$split$map4[1];
          styleMap.set(property, val);
        });
        mergedAttributes[key] = Array.from(styleMap.entries()).map(function (_ref15) {
          var _ref16 = _slicedToArray(_ref15, 2),
            property = _ref16[0],
            val = _ref16[1];
          return "".concat(property, ": ").concat(val);
        }).join('; ');
      } else {
        mergedAttributes[key] = value;
      }
    });
    return mergedAttributes;
  }, {});
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
function isFunction(value) {
  return typeof value === 'function';
}

/**
 * Optionally calls `value` as a function.
 * Otherwise it is returned directly.
 * @param value Function or any value.
 * @param context Optional context to bind to function.
 * @param props Optional props to pass to function.
 */
function callOrReturn(value) {
  var context = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : undefined;
  if (isFunction(value)) {
    for (var _len4 = arguments.length, props = new Array(_len4 > 2 ? _len4 - 2 : 0), _key4 = 2; _key4 < _len4; _key4++) {
      props[_key4 - 2] = arguments[_key4];
    }
    if (context) {
      return value.bind(context).apply(void 0, props);
    }
    return value.apply(void 0, props);
  }
  return value;
}
function isRegExp(value) {
  return Object.prototype.toString.call(value) === '[object RegExp]';
}

// see: https://github.com/mesqueeb/is-what/blob/88d6e4ca92fb2baab6003c54e02eedf4e729e5ab/src/index.ts
function getType(value) {
  return Object.prototype.toString.call(value).slice(8, -1);
}
function isPlainObject(value) {
  if (getType(value) !== 'Object') {
    return false;
  }
  return value.constructor === Object && Object.getPrototypeOf(value) === Object.prototype;
}
function mergeDeep(target, source) {
  var output = _objectSpread2({}, target);
  if (isPlainObject(target) && isPlainObject(source)) {
    Object.keys(source).forEach(function (key) {
      if (isPlainObject(source[key]) && isPlainObject(target[key])) {
        output[key] = mergeDeep(target[key], source[key]);
      } else {
        output[key] = source[key];
      }
    });
  }
  return output;
}
/**
 * The Extension class is the base class for all extensions.
 * @see https://tiptap.dev/api/extensions#create-a-new-extension
 */
var Extension = /*#__PURE__*/function () {
  function Extension() {
    var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    _classCallCheck(this, Extension);
    this.type = 'extension';
    this.name = 'extension';
    this.parent = null;
    this.child = null;
    this.config = {
      name: this.name,
      defaultOptions: {}
    };
    this.config = _objectSpread2(_objectSpread2({}, this.config), config);
    this.name = this.config.name;
    if (config.defaultOptions && Object.keys(config.defaultOptions).length > 0) {
      console.warn("[tiptap warn]: BREAKING CHANGE: \"defaultOptions\" is deprecated. Please use \"addOptions\" instead. Found in extension: \"".concat(this.name, "\"."));
    }
    // TODO: remove `addOptions` fallback
    this.options = this.config.defaultOptions;
    if (this.config.addOptions) {
      this.options = callOrReturn(getExtensionField(this, 'addOptions', {
        name: this.name
      }));
    }
    this.storage = callOrReturn(getExtensionField(this, 'addStorage', {
      name: this.name,
      options: this.options
    })) || {};
  }
  return _createClass(Extension, [{
    key: "configure",
    value: function configure() {
      var _this15 = this;
      var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      // return a new instance so we can use the same extension
      // with different calls of `configure`
      var extension = this.extend(_objectSpread2(_objectSpread2({}, this.config), {}, {
        addOptions: function addOptions() {
          return mergeDeep(_this15.options, options);
        }
      }));
      // Always preserve the current name
      extension.name = this.name;
      // Set the parent to be our parent
      extension.parent = this.parent;
      return extension;
    }
  }, {
    key: "extend",
    value: function extend() {
      var extendedConfig = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var extension = new Extension(_objectSpread2(_objectSpread2({}, this.config), extendedConfig));
      extension.parent = this;
      this.child = extension;
      extension.name = extendedConfig.name ? extendedConfig.name : extension.parent.name;
      if (extendedConfig.defaultOptions && Object.keys(extendedConfig.defaultOptions).length > 0) {
        console.warn("[tiptap warn]: BREAKING CHANGE: \"defaultOptions\" is deprecated. Please use \"addOptions\" instead. Found in extension: \"".concat(extension.name, "\"."));
      }
      extension.options = callOrReturn(getExtensionField(extension, 'addOptions', {
        name: extension.name
      }));
      extension.storage = callOrReturn(getExtensionField(extension, 'addStorage', {
        name: extension.name,
        options: extension.options
      }));
      return extension;
    }
  }], [{
    key: "create",
    value: function create() {
      var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return new Extension(config);
    }
  }]);
}();
/**
 * Gets the text between two positions in a Prosemirror node
 * and serializes it using the given text serializers and block separator (see getText)
 * @param startNode The Prosemirror node to start from
 * @param range The range of the text to get
 * @param options Options for the text serializer & block separator
 * @returns The text between the two positions
 */
function getTextBetween(startNode, range, options) {
  var from = range.from,
    to = range.to;
  var _ref24 = options || {},
    _ref24$blockSeparator = _ref24.blockSeparator,
    blockSeparator = _ref24$blockSeparator === void 0 ? '\n\n' : _ref24$blockSeparator,
    _ref24$textSerializer = _ref24.textSerializers,
    textSerializers = _ref24$textSerializer === void 0 ? {} : _ref24$textSerializer;
  var text = '';
  startNode.nodesBetween(from, to, function (node, pos, parent, index) {
    var _a;
    if (node.isBlock && pos > from) {
      text += blockSeparator;
    }
    var textSerializer = textSerializers === null || textSerializers === void 0 ? void 0 : textSerializers[node.type.name];
    if (textSerializer) {
      if (parent) {
        text += textSerializer({
          node: node,
          pos: pos,
          parent: parent,
          index: index,
          range: range
        });
      }
      // do not descend into child nodes when there exists a serializer
      return false;
    }
    if (node.isText) {
      text += (_a = node === null || node === void 0 ? void 0 : node.text) === null || _a === void 0 ? void 0 : _a.slice(Math.max(from, pos) - pos, to - pos); // eslint-disable-line
    }
  });
  return text;
}

/**
 * Find text serializers `toText` in a Prosemirror schema
 * @param schema The Prosemirror schema to search in
 * @returns A record of text serializers by node name
 */
function getTextSerializersFromSchema(schema) {
  return Object.fromEntries(Object.entries(schema.nodes).filter(function (_ref25) {
    var _ref26 = _slicedToArray(_ref25, 2),
      node = _ref26[1];
    return node.spec.toText;
  }).map(function (_ref27) {
    var _ref28 = _slicedToArray(_ref27, 2),
      name = _ref28[0],
      node = _ref28[1];
    return [name, node.spec.toText];
  }));
}
var ClipboardTextSerializer = Extension.create({
  name: 'clipboardTextSerializer',
  addOptions: function addOptions() {
    return {
      blockSeparator: undefined
    };
  },
  addProseMirrorPlugins: function addProseMirrorPlugins() {
    var _this16 = this;
    return [new Plugin({
      key: new PluginKey('clipboardTextSerializer'),
      props: {
        clipboardTextSerializer: function clipboardTextSerializer() {
          var editor = _this16.editor;
          var state = editor.state,
            schema = editor.schema;
          var doc = state.doc,
            selection = state.selection;
          var ranges = selection.ranges;
          var from = Math.min.apply(Math, _toConsumableArray(ranges.map(function (range) {
            return range.$from.pos;
          })));
          var to = Math.max.apply(Math, _toConsumableArray(ranges.map(function (range) {
            return range.$to.pos;
          })));
          var textSerializers = getTextSerializersFromSchema(schema);
          var range = {
            from: from,
            to: to
          };
          return getTextBetween(doc, range, _objectSpread2(_objectSpread2({}, _this16.options.blockSeparator !== undefined ? {
            blockSeparator: _this16.options.blockSeparator
          } : {}), {}, {
            textSerializers: textSerializers
          }));
        }
      }
    })];
  }
});
var blur = function blur() {
  return function (_ref29) {
    var editor = _ref29.editor,
      view = _ref29.view;
    requestAnimationFrame(function () {
      var _a;
      if (!editor.isDestroyed) {
        view.dom.blur();
        // Browsers should remove the caret on blur but safari does not.
        // See: https://github.com/ueberdosis/tiptap/issues/2405
        (_a = window === null || window === void 0 ? void 0 : window.getSelection()) === null || _a === void 0 ? void 0 : _a.removeAllRanges();
      }
    });
    return true;
  };
};
var clearContent = function clearContent() {
  var emitUpdate = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
  return function (_ref30) {
    var commands = _ref30.commands;
    return commands.setContent('', emitUpdate);
  };
};
var clearNodes = function clearNodes() {
  return function (_ref31) {
    var state = _ref31.state,
      tr = _ref31.tr,
      dispatch = _ref31.dispatch;
    var selection = tr.selection;
    var ranges = selection.ranges;
    if (!dispatch) {
      return true;
    }
    ranges.forEach(function (_ref32) {
      var $from = _ref32.$from,
        $to = _ref32.$to;
      state.doc.nodesBetween($from.pos, $to.pos, function (node, pos) {
        if (node.type.isText) {
          return;
        }
        var doc = tr.doc,
          mapping = tr.mapping;
        var $mappedFrom = doc.resolve(mapping.map(pos));
        var $mappedTo = doc.resolve(mapping.map(pos + node.nodeSize));
        var nodeRange = $mappedFrom.blockRange($mappedTo);
        if (!nodeRange) {
          return;
        }
        var targetLiftDepth = liftTarget(nodeRange);
        if (node.type.isTextblock) {
          var _$mappedFrom$parent$c = $mappedFrom.parent.contentMatchAt($mappedFrom.index()),
            defaultType = _$mappedFrom$parent$c.defaultType;
          tr.setNodeMarkup(nodeRange.start, defaultType);
        }
        if (targetLiftDepth || targetLiftDepth === 0) {
          tr.lift(nodeRange, targetLiftDepth);
        }
      });
    });
    return true;
  };
};
var command = function command(fn) {
  return function (props) {
    return fn(props);
  };
};
var createParagraphNear = function createParagraphNear() {
  return function (_ref33) {
    var state = _ref33.state,
      dispatch = _ref33.dispatch;
    return createParagraphNear$1(state, dispatch);
  };
};
var cut = function cut(originRange, targetPos) {
  return function (_ref34) {
    var editor = _ref34.editor,
      tr = _ref34.tr;
    var state = editor.state;
    var contentSlice = state.doc.slice(originRange.from, originRange.to);
    tr.deleteRange(originRange.from, originRange.to);
    var newPos = tr.mapping.map(targetPos);
    tr.insert(newPos, contentSlice.content);
    tr.setSelection(new TextSelection(tr.doc.resolve(newPos - 1)));
    return true;
  };
};
var deleteCurrentNode = function deleteCurrentNode() {
  return function (_ref35) {
    var tr = _ref35.tr,
      dispatch = _ref35.dispatch;
    var selection = tr.selection;
    var currentNode = selection.$anchor.node();
    // if there is content inside the current node, break out of this command
    if (currentNode.content.size > 0) {
      return false;
    }
    var $pos = tr.selection.$anchor;
    for (var depth = $pos.depth; depth > 0; depth -= 1) {
      var node = $pos.node(depth);
      if (node.type === currentNode.type) {
        if (dispatch) {
          var from = $pos.before(depth);
          var to = $pos.after(depth);
          tr["delete"](from, to).scrollIntoView();
        }
        return true;
      }
    }
    return false;
  };
};
var deleteNode = function deleteNode(typeOrName) {
  return function (_ref36) {
    var tr = _ref36.tr,
      state = _ref36.state,
      dispatch = _ref36.dispatch;
    var type = getNodeType(typeOrName, state.schema);
    var $pos = tr.selection.$anchor;
    for (var depth = $pos.depth; depth > 0; depth -= 1) {
      var node = $pos.node(depth);
      if (node.type === type) {
        if (dispatch) {
          var from = $pos.before(depth);
          var to = $pos.after(depth);
          tr["delete"](from, to).scrollIntoView();
        }
        return true;
      }
    }
    return false;
  };
};
var deleteRange = function deleteRange(range) {
  return function (_ref37) {
    var tr = _ref37.tr,
      dispatch = _ref37.dispatch;
    var from = range.from,
      to = range.to;
    if (dispatch) {
      tr["delete"](from, to);
    }
    return true;
  };
};
var deleteSelection = function deleteSelection() {
  return function (_ref38) {
    var state = _ref38.state,
      dispatch = _ref38.dispatch;
    return deleteSelection$1(state, dispatch);
  };
};
var enter = function enter() {
  return function (_ref39) {
    var commands = _ref39.commands;
    return commands.keyboardShortcut('Enter');
  };
};
var exitCode = function exitCode() {
  return function (_ref40) {
    var state = _ref40.state,
      dispatch = _ref40.dispatch;
    return exitCode$1(state, dispatch);
  };
};

/**
 * Check if object1 includes object2
 * @param object1 Object
 * @param object2 Object
 */
function objectIncludes(object1, object2) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {
    strict: true
  };
  var keys = Object.keys(object2);
  if (!keys.length) {
    return true;
  }
  return keys.every(function (key) {
    if (options.strict) {
      return object2[key] === object1[key];
    }
    if (isRegExp(object2[key])) {
      return object2[key].test(object1[key]);
    }
    return object2[key] === object1[key];
  });
}
function findMarkInSet(marks, type) {
  var attributes = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return marks.find(function (item) {
    return item.type === type && objectIncludes(
    // Only check equality for the attributes that are provided
    Object.fromEntries(Object.keys(attributes).map(function (k) {
      return [k, item.attrs[k]];
    })), attributes);
  });
}
function isMarkInSet(marks, type) {
  var attributes = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return !!findMarkInSet(marks, type, attributes);
}
/**
 * Get the range of a mark at a resolved position.
 */
function getMarkRange(
/**
 * The position to get the mark range for.
 */
$pos,
/**
 * The mark type to get the range for.
 */
type,
/**
 * The attributes to match against.
 * If not provided, only the first mark at the position will be matched.
 */
attributes) {
  var _a;
  if (!$pos || !type) {
    return;
  }
  var start = $pos.parent.childAfter($pos.parentOffset);
  // If the cursor is at the start of a text node that does not have the mark, look backward
  if (!start.node || !start.node.marks.some(function (mark) {
    return mark.type === type;
  })) {
    start = $pos.parent.childBefore($pos.parentOffset);
  }
  // If there is no text node with the mark even backward, return undefined
  if (!start.node || !start.node.marks.some(function (mark) {
    return mark.type === type;
  })) {
    return;
  }
  // Default to only matching against the first mark's attributes
  attributes = attributes || ((_a = start.node.marks[0]) === null || _a === void 0 ? void 0 : _a.attrs);
  // We now know that the cursor is either at the start, middle or end of a text node with the specified mark
  // so we can look it up on the targeted mark
  var mark = findMarkInSet(_toConsumableArray(start.node.marks), type, attributes);
  if (!mark) {
    return;
  }
  var startIndex = start.index;
  var startPos = $pos.start() + start.offset;
  var endIndex = startIndex + 1;
  var endPos = startPos + start.node.nodeSize;
  while (startIndex > 0 && isMarkInSet(_toConsumableArray($pos.parent.child(startIndex - 1).marks), type, attributes)) {
    startIndex -= 1;
    startPos -= $pos.parent.child(startIndex).nodeSize;
  }
  while (endIndex < $pos.parent.childCount && isMarkInSet(_toConsumableArray($pos.parent.child(endIndex).marks), type, attributes)) {
    endPos += $pos.parent.child(endIndex).nodeSize;
    endIndex += 1;
  }
  return {
    from: startPos,
    to: endPos
  };
}
function getMarkType(nameOrType, schema) {
  if (typeof nameOrType === 'string') {
    if (!schema.marks[nameOrType]) {
      throw Error("There is no mark type named '".concat(nameOrType, "'. Maybe you forgot to add the extension?"));
    }
    return schema.marks[nameOrType];
  }
  return nameOrType;
}
var extendMarkRange = function extendMarkRange(typeOrName) {
  var attributes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return function (_ref41) {
    var tr = _ref41.tr,
      state = _ref41.state,
      dispatch = _ref41.dispatch;
    var type = getMarkType(typeOrName, state.schema);
    var doc = tr.doc,
      selection = tr.selection;
    var $from = selection.$from,
      from = selection.from,
      to = selection.to;
    if (dispatch) {
      var range = getMarkRange($from, type, attributes);
      if (range && range.from <= from && range.to >= to) {
        var newSelection = TextSelection.create(doc, range.from, range.to);
        tr.setSelection(newSelection);
      }
    }
    return true;
  };
};
var first = function first(commands) {
  return function (props) {
    var items = typeof commands === 'function' ? commands(props) : commands;
    for (var i = 0; i < items.length; i += 1) {
      if (items[i](props)) {
        return true;
      }
    }
    return false;
  };
};
function isTextSelection(value) {
  return value instanceof TextSelection;
}
function minMax() {
  var value = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  var min = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var max = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  return Math.min(Math.max(value, min), max);
}
function resolveFocusPosition(doc) {
  var position = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  if (!position) {
    return null;
  }
  var selectionAtStart = Selection.atStart(doc);
  var selectionAtEnd = Selection.atEnd(doc);
  if (position === 'start' || position === true) {
    return selectionAtStart;
  }
  if (position === 'end') {
    return selectionAtEnd;
  }
  var minPos = selectionAtStart.from;
  var maxPos = selectionAtEnd.to;
  if (position === 'all') {
    return TextSelection.create(doc, minMax(0, minPos, maxPos), minMax(doc.content.size, minPos, maxPos));
  }
  return TextSelection.create(doc, minMax(position, minPos, maxPos), minMax(position, minPos, maxPos));
}
function isAndroid() {
  return navigator.platform === 'Android' || /android/i.test(navigator.userAgent);
}
function isiOS() {
  return ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform)
  // iPad on iOS 13 detection
  || navigator.userAgent.includes('Mac') && 'ontouchend' in document;
}
var focus = function focus() {
  var position = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return function (_ref42) {
    var editor = _ref42.editor,
      view = _ref42.view,
      tr = _ref42.tr,
      dispatch = _ref42.dispatch;
    options = _objectSpread2({
      scrollIntoView: true
    }, options);
    var delayedFocus = function delayedFocus() {
      // focus within `requestAnimationFrame` breaks focus on iOS and Android
      // so we have to call this
      if (isiOS() || isAndroid()) {
        view.dom.focus();
      }
      // For React we have to focus asynchronously. Otherwise wild things happen.
      // see: https://github.com/ueberdosis/tiptap/issues/1520
      requestAnimationFrame(function () {
        if (!editor.isDestroyed) {
          view.focus();
          if (options === null || options === void 0 ? void 0 : options.scrollIntoView) {
            editor.commands.scrollIntoView();
          }
        }
      });
    };
    if (view.hasFocus() && position === null || position === false) {
      return true;
    }
    // we don’t try to resolve a NodeSelection or CellSelection
    if (dispatch && position === null && !isTextSelection(editor.state.selection)) {
      delayedFocus();
      return true;
    }
    // pass through tr.doc instead of editor.state.doc
    // since transactions could change the editors state before this command has been run
    var selection = resolveFocusPosition(tr.doc, position) || editor.state.selection;
    var isSameSelection = editor.state.selection.eq(selection);
    if (dispatch) {
      if (!isSameSelection) {
        tr.setSelection(selection);
      }
      // `tr.setSelection` resets the stored marks
      // so we’ll restore them if the selection is the same as before
      if (isSameSelection && tr.storedMarks) {
        tr.setStoredMarks(tr.storedMarks);
      }
      delayedFocus();
    }
    return true;
  };
};
var forEach = function forEach(items, fn) {
  return function (props) {
    return items.every(function (item, index) {
      return fn(item, _objectSpread2(_objectSpread2({}, props), {}, {
        index: index
      }));
    });
  };
};
var insertContent = function insertContent(value, options) {
  return function (_ref43) {
    var tr = _ref43.tr,
      commands = _ref43.commands;
    return commands.insertContentAt({
      from: tr.selection.from,
      to: tr.selection.to
    }, value, options);
  };
};
var _removeWhitespaces = function removeWhitespaces(node) {
  var children = node.childNodes;
  for (var i = children.length - 1; i >= 0; i -= 1) {
    var child = children[i];
    if (child.nodeType === 3 && child.nodeValue && /^(\n\s\s|\n)$/.test(child.nodeValue)) {
      node.removeChild(child);
    } else if (child.nodeType === 1) {
      _removeWhitespaces(child);
    }
  }
  return node;
};
function elementFromString(value) {
  // add a wrapper to preserve leading and trailing whitespace
  var wrappedValue = "<body>".concat(value, "</body>");
  var html = new window.DOMParser().parseFromString(wrappedValue, 'text/html').body;
  return _removeWhitespaces(html);
}

/**
 * Takes a JSON or HTML content and creates a Prosemirror node or fragment from it.
 * @param content The JSON or HTML content to create the node from
 * @param schema The Prosemirror schema to use for the node
 * @param options Options for the parser
 * @returns The created Prosemirror node or fragment
 */
function createNodeFromContent(content, schema, options) {
  if (content instanceof Node$1 || content instanceof Fragment) {
    return content;
  }
  options = _objectSpread2({
    slice: true,
    parseOptions: {}
  }, options);
  var isJSONContent = _typeof(content) === 'object' && content !== null;
  var isTextContent = typeof content === 'string';
  if (isJSONContent) {
    try {
      var isArrayContent = Array.isArray(content) && content.length > 0;
      // if the JSON Content is an array of nodes, create a fragment for each node
      if (isArrayContent) {
        return Fragment.fromArray(content.map(function (item) {
          return schema.nodeFromJSON(item);
        }));
      }
      var node = schema.nodeFromJSON(content);
      if (options.errorOnInvalidContent) {
        node.check();
      }
      return node;
    } catch (error) {
      if (options.errorOnInvalidContent) {
        throw new Error('[tiptap error]: Invalid JSON content', {
          cause: error
        });
      }
      console.warn('[tiptap warn]: Invalid content.', 'Passed value:', content, 'Error:', error);
      return createNodeFromContent('', schema, options);
    }
  }
  if (isTextContent) {
    // Check for invalid content
    if (options.errorOnInvalidContent) {
      var hasInvalidContent = false;
      var invalidContent = '';
      // A copy of the current schema with a catch-all node at the end
      var contentCheckSchema = new Schema({
        topNode: schema.spec.topNode,
        marks: schema.spec.marks,
        // Prosemirror's schemas are executed such that: the last to execute, matches last
        // This means that we can add a catch-all node at the end of the schema to catch any content that we don't know how to handle
        nodes: schema.spec.nodes.append({
          __tiptap__private__unknown__catch__all__node: {
            content: 'inline*',
            group: 'block',
            parseDOM: [{
              tag: '*',
              getAttrs: function getAttrs(e) {
                // If this is ever called, we know that the content has something that we don't know how to handle in the schema
                hasInvalidContent = true;
                // Try to stringify the element for a more helpful error message
                invalidContent = typeof e === 'string' ? e : e.outerHTML;
                return null;
              }
            }]
          }
        })
      });
      if (options.slice) {
        DOMParser.fromSchema(contentCheckSchema).parseSlice(elementFromString(content), options.parseOptions);
      } else {
        DOMParser.fromSchema(contentCheckSchema).parse(elementFromString(content), options.parseOptions);
      }
      if (options.errorOnInvalidContent && hasInvalidContent) {
        throw new Error('[tiptap error]: Invalid HTML content', {
          cause: new Error("Invalid element found: ".concat(invalidContent))
        });
      }
    }
    var parser = DOMParser.fromSchema(schema);
    if (options.slice) {
      return parser.parseSlice(elementFromString(content), options.parseOptions).content;
    }
    return parser.parse(elementFromString(content), options.parseOptions);
  }
  return createNodeFromContent('', schema, options);
}

// source: https://github.com/ProseMirror/prosemirror-state/blob/master/src/selection.js#L466
function selectionToInsertionEnd(tr, startLen, bias) {
  var last = tr.steps.length - 1;
  if (last < startLen) {
    return;
  }
  var step = tr.steps[last];
  if (!(step instanceof ReplaceStep || step instanceof ReplaceAroundStep)) {
    return;
  }
  var map = tr.mapping.maps[last];
  var end = 0;
  map.forEach(function (_from, _to, _newFrom, newTo) {
    if (end === 0) {
      end = newTo;
    }
  });
  tr.setSelection(Selection.near(tr.doc.resolve(end), bias));
}
var isFragment = function isFragment(nodeOrFragment) {
  return !('type' in nodeOrFragment);
};
var insertContentAt = function insertContentAt(position, value, options) {
  return function (_ref44) {
    var tr = _ref44.tr,
      dispatch = _ref44.dispatch,
      editor = _ref44.editor;
    var _a;
    if (dispatch) {
      options = _objectSpread2({
        parseOptions: editor.options.parseOptions,
        updateSelection: true,
        applyInputRules: false,
        applyPasteRules: false
      }, options);
      var content;
      try {
        content = createNodeFromContent(value, editor.schema, {
          parseOptions: _objectSpread2({
            preserveWhitespace: 'full'
          }, options.parseOptions),
          errorOnInvalidContent: (_a = options.errorOnInvalidContent) !== null && _a !== void 0 ? _a : editor.options.enableContentCheck
        });
      } catch (e) {
        editor.emit('contentError', {
          editor: editor,
          error: e,
          disableCollaboration: function disableCollaboration() {
            if (editor.storage.collaboration) {
              editor.storage.collaboration.isDisabled = true;
            }
          }
        });
        return false;
      }
      var _ref45 = typeof position === 'number' ? {
          from: position,
          to: position
        } : {
          from: position.from,
          to: position.to
        },
        from = _ref45.from,
        to = _ref45.to;
      var isOnlyTextContent = true;
      var isOnlyBlockContent = true;
      var nodes = isFragment(content) ? content : [content];
      nodes.forEach(function (node) {
        // check if added node is valid
        node.check();
        isOnlyTextContent = isOnlyTextContent ? node.isText && node.marks.length === 0 : false;
        isOnlyBlockContent = isOnlyBlockContent ? node.isBlock : false;
      });
      // check if we can replace the wrapping node by
      // the newly inserted content
      // example:
      // replace an empty paragraph by an inserted image
      // instead of inserting the image below the paragraph
      if (from === to && isOnlyBlockContent) {
        var _tr$doc$resolve = tr.doc.resolve(from),
          parent = _tr$doc$resolve.parent;
        var isEmptyTextBlock = parent.isTextblock && !parent.type.spec.code && !parent.childCount;
        if (isEmptyTextBlock) {
          from -= 1;
          to += 1;
        }
      }
      var newContent;
      // if there is only plain text we have to use `insertText`
      // because this will keep the current marks
      if (isOnlyTextContent) {
        // if value is string, we can use it directly
        // otherwise if it is an array, we have to join it
        if (Array.isArray(value)) {
          newContent = value.map(function (v) {
            return v.text || '';
          }).join('');
        } else if (value instanceof Fragment) {
          var text = '';
          value.forEach(function (node) {
            if (node.text) {
              text += node.text;
            }
          });
          newContent = text;
        } else if (_typeof(value) === 'object' && !!value && !!value.text) {
          newContent = value.text;
        } else {
          newContent = value;
        }
        tr.insertText(newContent, from, to);
      } else {
        newContent = content;
        tr.replaceWith(from, to, newContent);
      }
      // set cursor at end of inserted content
      if (options.updateSelection) {
        selectionToInsertionEnd(tr, tr.steps.length - 1, -1);
      }
      if (options.applyInputRules) {
        tr.setMeta('applyInputRules', {
          from: from,
          text: newContent
        });
      }
      if (options.applyPasteRules) {
        tr.setMeta('applyPasteRules', {
          from: from,
          text: newContent
        });
      }
    }
    return true;
  };
};
var joinUp = function joinUp() {
  return function (_ref46) {
    var state = _ref46.state,
      dispatch = _ref46.dispatch;
    return joinUp$1(state, dispatch);
  };
};
var joinDown = function joinDown() {
  return function (_ref47) {
    var state = _ref47.state,
      dispatch = _ref47.dispatch;
    return joinDown$1(state, dispatch);
  };
};
var joinBackward = function joinBackward() {
  return function (_ref48) {
    var state = _ref48.state,
      dispatch = _ref48.dispatch;
    return joinBackward$1(state, dispatch);
  };
};
var joinForward = function joinForward() {
  return function (_ref49) {
    var state = _ref49.state,
      dispatch = _ref49.dispatch;
    return joinForward$1(state, dispatch);
  };
};
var joinItemBackward = function joinItemBackward() {
  return function (_ref50) {
    var state = _ref50.state,
      dispatch = _ref50.dispatch,
      tr = _ref50.tr;
    try {
      var point = joinPoint(state.doc, state.selection.$from.pos, -1);
      if (point === null || point === undefined) {
        return false;
      }
      tr.join(point, 2);
      if (dispatch) {
        dispatch(tr);
      }
      return true;
    } catch (_unused3) {
      return false;
    }
  };
};
var joinItemForward = function joinItemForward() {
  return function (_ref51) {
    var state = _ref51.state,
      dispatch = _ref51.dispatch,
      tr = _ref51.tr;
    try {
      var point = joinPoint(state.doc, state.selection.$from.pos, +1);
      if (point === null || point === undefined) {
        return false;
      }
      tr.join(point, 2);
      if (dispatch) {
        dispatch(tr);
      }
      return true;
    } catch (_unused4) {
      return false;
    }
  };
};
var joinTextblockBackward = function joinTextblockBackward() {
  return function (_ref52) {
    var state = _ref52.state,
      dispatch = _ref52.dispatch;
    return joinTextblockBackward$1(state, dispatch);
  };
};
var joinTextblockForward = function joinTextblockForward() {
  return function (_ref53) {
    var state = _ref53.state,
      dispatch = _ref53.dispatch;
    return joinTextblockForward$1(state, dispatch);
  };
};
function isMacOS() {
  return typeof navigator !== 'undefined' ? /Mac/.test(navigator.platform) : false;
}
function normalizeKeyName(name) {
  var parts = name.split(/-(?!$)/);
  var result = parts[parts.length - 1];
  if (result === 'Space') {
    result = ' ';
  }
  var alt;
  var ctrl;
  var shift;
  var meta;
  for (var i = 0; i < parts.length - 1; i += 1) {
    var mod = parts[i];
    if (/^(cmd|meta|m)$/i.test(mod)) {
      meta = true;
    } else if (/^a(lt)?$/i.test(mod)) {
      alt = true;
    } else if (/^(c|ctrl|control)$/i.test(mod)) {
      ctrl = true;
    } else if (/^s(hift)?$/i.test(mod)) {
      shift = true;
    } else if (/^mod$/i.test(mod)) {
      if (isiOS() || isMacOS()) {
        meta = true;
      } else {
        ctrl = true;
      }
    } else {
      throw new Error("Unrecognized modifier name: ".concat(mod));
    }
  }
  if (alt) {
    result = "Alt-".concat(result);
  }
  if (ctrl) {
    result = "Ctrl-".concat(result);
  }
  if (meta) {
    result = "Meta-".concat(result);
  }
  if (shift) {
    result = "Shift-".concat(result);
  }
  return result;
}
var keyboardShortcut = function keyboardShortcut(name) {
  return function (_ref54) {
    var editor = _ref54.editor,
      view = _ref54.view,
      tr = _ref54.tr,
      dispatch = _ref54.dispatch;
    var keys = normalizeKeyName(name).split(/-(?!$)/);
    var key = keys.find(function (item) {
      return !['Alt', 'Ctrl', 'Meta', 'Shift'].includes(item);
    });
    var event = new KeyboardEvent('keydown', {
      key: key === 'Space' ? ' ' : key,
      altKey: keys.includes('Alt'),
      ctrlKey: keys.includes('Ctrl'),
      metaKey: keys.includes('Meta'),
      shiftKey: keys.includes('Shift'),
      bubbles: true,
      cancelable: true
    });
    var capturedTransaction = editor.captureTransaction(function () {
      view.someProp('handleKeyDown', function (f) {
        return f(view, event);
      });
    });
    capturedTransaction === null || capturedTransaction === void 0 ? void 0 : capturedTransaction.steps.forEach(function (step) {
      var newStep = step.map(tr.mapping);
      if (newStep && dispatch) {
        tr.maybeStep(newStep);
      }
    });
    return true;
  };
};
function isNodeActive(state, typeOrName) {
  var attributes = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var _state$selection = state.selection,
    from = _state$selection.from,
    to = _state$selection.to,
    empty = _state$selection.empty;
  var type = typeOrName ? getNodeType(typeOrName, state.schema) : null;
  var nodeRanges = [];
  state.doc.nodesBetween(from, to, function (node, pos) {
    if (node.isText) {
      return;
    }
    var relativeFrom = Math.max(from, pos);
    var relativeTo = Math.min(to, pos + node.nodeSize);
    nodeRanges.push({
      node: node,
      from: relativeFrom,
      to: relativeTo
    });
  });
  var selectionRange = to - from;
  var matchedNodeRanges = nodeRanges.filter(function (nodeRange) {
    if (!type) {
      return true;
    }
    return type.name === nodeRange.node.type.name;
  }).filter(function (nodeRange) {
    return objectIncludes(nodeRange.node.attrs, attributes, {
      strict: false
    });
  });
  if (empty) {
    return !!matchedNodeRanges.length;
  }
  var range = matchedNodeRanges.reduce(function (sum, nodeRange) {
    return sum + nodeRange.to - nodeRange.from;
  }, 0);
  return range >= selectionRange;
}
var lift = function lift(typeOrName) {
  var attributes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return function (_ref55) {
    var state = _ref55.state,
      dispatch = _ref55.dispatch;
    var type = getNodeType(typeOrName, state.schema);
    var isActive = isNodeActive(state, type, attributes);
    if (!isActive) {
      return false;
    }
    return lift$1(state, dispatch);
  };
};
var liftEmptyBlock = function liftEmptyBlock() {
  return function (_ref56) {
    var state = _ref56.state,
      dispatch = _ref56.dispatch;
    return liftEmptyBlock$1(state, dispatch);
  };
};
var liftListItem = function liftListItem(typeOrName) {
  return function (_ref57) {
    var state = _ref57.state,
      dispatch = _ref57.dispatch;
    var type = getNodeType(typeOrName, state.schema);
    return liftListItem$1(type)(state, dispatch);
  };
};
var newlineInCode = function newlineInCode() {
  return function (_ref58) {
    var state = _ref58.state,
      dispatch = _ref58.dispatch;
    return newlineInCode$1(state, dispatch);
  };
};

/**
 * Get the type of a schema item by its name.
 * @param name The name of the schema item
 * @param schema The Prosemiror schema to search in
 * @returns The type of the schema item (`node` or `mark`), or null if it doesn't exist
 */
function getSchemaTypeNameByName(name, schema) {
  if (schema.nodes[name]) {
    return 'node';
  }
  if (schema.marks[name]) {
    return 'mark';
  }
  return null;
}

/**
 * Remove a property or an array of properties from an object
 * @param obj Object
 * @param key Key to remove
 */
function deleteProps(obj, propOrProps) {
  var props = typeof propOrProps === 'string' ? [propOrProps] : propOrProps;
  return Object.keys(obj).reduce(function (newObj, prop) {
    if (!props.includes(prop)) {
      newObj[prop] = obj[prop];
    }
    return newObj;
  }, {});
}
var resetAttributes = function resetAttributes(typeOrName, attributes) {
  return function (_ref59) {
    var tr = _ref59.tr,
      state = _ref59.state,
      dispatch = _ref59.dispatch;
    var nodeType = null;
    var markType = null;
    var schemaType = getSchemaTypeNameByName(typeof typeOrName === 'string' ? typeOrName : typeOrName.name, state.schema);
    if (!schemaType) {
      return false;
    }
    if (schemaType === 'node') {
      nodeType = getNodeType(typeOrName, state.schema);
    }
    if (schemaType === 'mark') {
      markType = getMarkType(typeOrName, state.schema);
    }
    if (dispatch) {
      tr.selection.ranges.forEach(function (range) {
        state.doc.nodesBetween(range.$from.pos, range.$to.pos, function (node, pos) {
          if (nodeType && nodeType === node.type) {
            tr.setNodeMarkup(pos, undefined, deleteProps(node.attrs, attributes));
          }
          if (markType && node.marks.length) {
            node.marks.forEach(function (mark) {
              if (markType === mark.type) {
                tr.addMark(pos, pos + node.nodeSize, markType.create(deleteProps(mark.attrs, attributes)));
              }
            });
          }
        });
      });
    }
    return true;
  };
};
var scrollIntoView = function scrollIntoView() {
  return function (_ref60) {
    var tr = _ref60.tr,
      dispatch = _ref60.dispatch;
    if (dispatch) {
      tr.scrollIntoView();
    }
    return true;
  };
};
var selectAll = function selectAll() {
  return function (_ref61) {
    var tr = _ref61.tr,
      dispatch = _ref61.dispatch;
    if (dispatch) {
      var selection = new AllSelection(tr.doc);
      tr.setSelection(selection);
    }
    return true;
  };
};
var selectNodeBackward = function selectNodeBackward() {
  return function (_ref62) {
    var state = _ref62.state,
      dispatch = _ref62.dispatch;
    return selectNodeBackward$1(state, dispatch);
  };
};
var selectNodeForward = function selectNodeForward() {
  return function (_ref63) {
    var state = _ref63.state,
      dispatch = _ref63.dispatch;
    return selectNodeForward$1(state, dispatch);
  };
};
var selectParentNode = function selectParentNode() {
  return function (_ref64) {
    var state = _ref64.state,
      dispatch = _ref64.dispatch;
    return selectParentNode$1(state, dispatch);
  };
};

// @ts-ignore
// TODO: add types to @types/prosemirror-commands
var selectTextblockEnd = function selectTextblockEnd() {
  return function (_ref65) {
    var state = _ref65.state,
      dispatch = _ref65.dispatch;
    return selectTextblockEnd$1(state, dispatch);
  };
};

// @ts-ignore
// TODO: add types to @types/prosemirror-commands
var selectTextblockStart = function selectTextblockStart() {
  return function (_ref66) {
    var state = _ref66.state,
      dispatch = _ref66.dispatch;
    return selectTextblockStart$1(state, dispatch);
  };
};

/**
 * Create a new Prosemirror document node from content.
 * @param content The JSON or HTML content to create the document from
 * @param schema The Prosemirror schema to use for the document
 * @param parseOptions Options for the parser
 * @returns The created Prosemirror document node
 */
function createDocument(content, schema) {
  var parseOptions = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  return createNodeFromContent(content, schema, {
    slice: false,
    parseOptions: parseOptions,
    errorOnInvalidContent: options.errorOnInvalidContent
  });
}
var setContent = function setContent(content) {
  var emitUpdate = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var parseOptions = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var options = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  return function (_ref67) {
    var editor = _ref67.editor,
      tr = _ref67.tr,
      dispatch = _ref67.dispatch,
      commands = _ref67.commands;
    var _a, _b;
    var doc = tr.doc;
    // This is to keep backward compatibility with the previous behavior
    // TODO remove this in the next major version
    if (parseOptions.preserveWhitespace !== 'full') {
      var _document = createDocument(content, editor.schema, parseOptions, {
        errorOnInvalidContent: (_a = options.errorOnInvalidContent) !== null && _a !== void 0 ? _a : editor.options.enableContentCheck
      });
      if (dispatch) {
        tr.replaceWith(0, doc.content.size, _document).setMeta('preventUpdate', !emitUpdate);
      }
      return true;
    }
    if (dispatch) {
      tr.setMeta('preventUpdate', !emitUpdate);
    }
    return commands.insertContentAt({
      from: 0,
      to: doc.content.size
    }, content, {
      parseOptions: parseOptions,
      errorOnInvalidContent: (_b = options.errorOnInvalidContent) !== null && _b !== void 0 ? _b : editor.options.enableContentCheck
    });
  };
};
function getMarkAttributes(state, typeOrName) {
  var type = getMarkType(typeOrName, state.schema);
  var _state$selection2 = state.selection,
    from = _state$selection2.from,
    to = _state$selection2.to,
    empty = _state$selection2.empty;
  var marks = [];
  if (empty) {
    if (state.storedMarks) {
      marks.push.apply(marks, _toConsumableArray(state.storedMarks));
    }
    marks.push.apply(marks, _toConsumableArray(state.selection.$head.marks()));
  } else {
    state.doc.nodesBetween(from, to, function (node) {
      marks.push.apply(marks, _toConsumableArray(node.marks));
    });
  }
  var mark = marks.find(function (markItem) {
    return markItem.type.name === type.name;
  });
  if (!mark) {
    return {};
  }
  return _objectSpread2({}, mark.attrs);
}

/**
 * Gets the default block type at a given match
 * @param match The content match to get the default block type from
 * @returns The default block type or null
 */
function defaultBlockAt(match) {
  for (var i = 0; i < match.edgeCount; i += 1) {
    var _match$edge = match.edge(i),
      type = _match$edge.type;
    if (type.isTextblock && !type.hasRequiredAttrs()) {
      return type;
    }
  }
  return null;
}

/**
 * Finds the closest parent node to a resolved position that matches a predicate.
 * @param $pos The resolved position to search from
 * @param predicate The predicate to match
 * @returns The closest parent node to the resolved position that matches the predicate
 * @example ```js
 * findParentNodeClosestToPos($from, node => node.type.name === 'paragraph')
 * ```
 */
function findParentNodeClosestToPos($pos, predicate) {
  for (var i = $pos.depth; i > 0; i -= 1) {
    var node = $pos.node(i);
    if (predicate(node)) {
      return {
        pos: i > 0 ? $pos.before(i) : 0,
        start: $pos.start(i),
        depth: i,
        node: node
      };
    }
  }
}

/**
 * Finds the closest parent node to the current selection that matches a predicate.
 * @param predicate The predicate to match
 * @returns A command that finds the closest parent node to the current selection that matches the predicate
 * @example ```js
 * findParentNode(node => node.type.name === 'paragraph')
 * ```
 */
function findParentNode(predicate) {
  return function (selection) {
    return findParentNodeClosestToPos(selection.$from, predicate);
  };
}

/**
 * Return attributes of an extension that should be splitted by keepOnSplit flag
 * @param extensionAttributes Array of extension attributes
 * @param typeName The type of the extension
 * @param attributes The attributes of the extension
 * @returns The splitted attributes
 */
function getSplittedAttributes(extensionAttributes, typeName, attributes) {
  return Object.fromEntries(Object.entries(attributes).filter(function (_ref70) {
    var _ref71 = _slicedToArray(_ref70, 1),
      name = _ref71[0];
    var extensionAttribute = extensionAttributes.find(function (item) {
      return item.type === typeName && item.name === name;
    });
    if (!extensionAttribute) {
      return false;
    }
    return extensionAttribute.attribute.keepOnSplit;
  }));
}
function isMarkActive(state, typeOrName) {
  var attributes = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var _state$selection4 = state.selection,
    empty = _state$selection4.empty,
    ranges = _state$selection4.ranges;
  var type = typeOrName ? getMarkType(typeOrName, state.schema) : null;
  if (empty) {
    return !!(state.storedMarks || state.selection.$from.marks()).filter(function (mark) {
      if (!type) {
        return true;
      }
      return type.name === mark.type.name;
    }).find(function (mark) {
      return objectIncludes(mark.attrs, attributes, {
        strict: false
      });
    });
  }
  var selectionRange = 0;
  var markRanges = [];
  ranges.forEach(function (_ref72) {
    var $from = _ref72.$from,
      $to = _ref72.$to;
    var from = $from.pos;
    var to = $to.pos;
    state.doc.nodesBetween(from, to, function (node, pos) {
      if (!node.isText && !node.marks.length) {
        return;
      }
      var relativeFrom = Math.max(from, pos);
      var relativeTo = Math.min(to, pos + node.nodeSize);
      var range = relativeTo - relativeFrom;
      selectionRange += range;
      markRanges.push.apply(markRanges, _toConsumableArray(node.marks.map(function (mark) {
        return {
          mark: mark,
          from: relativeFrom,
          to: relativeTo
        };
      })));
    });
  });
  if (selectionRange === 0) {
    return false;
  }
  // calculate range of matched mark
  var matchedRange = markRanges.filter(function (markRange) {
    if (!type) {
      return true;
    }
    return type.name === markRange.mark.type.name;
  }).filter(function (markRange) {
    return objectIncludes(markRange.mark.attrs, attributes, {
      strict: false
    });
  }).reduce(function (sum, markRange) {
    return sum + markRange.to - markRange.from;
  }, 0);
  // calculate range of marks that excludes the searched mark
  // for example `code` doesn’t allow any other marks
  var excludedRange = markRanges.filter(function (markRange) {
    if (!type) {
      return true;
    }
    return markRange.mark.type !== type && markRange.mark.type.excludes(type);
  }).reduce(function (sum, markRange) {
    return sum + markRange.to - markRange.from;
  }, 0);
  // we only include the result of `excludedRange`
  // if there is a match at all
  var range = matchedRange > 0 ? matchedRange + excludedRange : matchedRange;
  return range >= selectionRange;
}
function isList(name, extensions) {
  var _splitExtensions4 = splitExtensions(extensions),
    nodeExtensions = _splitExtensions4.nodeExtensions;
  var extension = nodeExtensions.find(function (item) {
    return item.name === name;
  });
  if (!extension) {
    return false;
  }
  var context = {
    name: extension.name,
    options: extension.options,
    storage: extension.storage
  };
  var group = callOrReturn(getExtensionField(extension, 'group', context));
  if (typeof group !== 'string') {
    return false;
  }
  return group.split(' ').includes('list');
}

/**
 * Returns true if the given prosemirror node is empty.
 */
function isNodeEmpty(node) {
  var _ref73 = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref73$checkChildren = _ref73.checkChildren,
    checkChildren = _ref73$checkChildren === void 0 ? true : _ref73$checkChildren,
    _ref73$ignoreWhitespa = _ref73.ignoreWhitespace,
    ignoreWhitespace = _ref73$ignoreWhitespa === void 0 ? false : _ref73$ignoreWhitespa;
  var _a;
  if (ignoreWhitespace) {
    if (node.type.name === 'hardBreak') {
      // Hard breaks are considered empty
      return true;
    }
    if (node.isText) {
      return /^\s*$/m.test((_a = node.text) !== null && _a !== void 0 ? _a : '');
    }
  }
  if (node.isText) {
    return !node.text;
  }
  if (node.isAtom || node.isLeaf) {
    return false;
  }
  if (node.content.childCount === 0) {
    return true;
  }
  if (checkChildren) {
    var isContentEmpty = true;
    node.content.forEach(function (childNode) {
      if (isContentEmpty === false) {
        // Exit early for perf
        return;
      }
      if (!isNodeEmpty(childNode, {
        ignoreWhitespace: ignoreWhitespace,
        checkChildren: checkChildren
      })) {
        isContentEmpty = false;
      }
    });
    return isContentEmpty;
  }
  return false;
}
function canSetMark(state, tr, newMarkType) {
  var _a;
  var selection = tr.selection;
  var cursor = null;
  if (isTextSelection(selection)) {
    cursor = selection.$cursor;
  }
  if (cursor) {
    var currentMarks = (_a = state.storedMarks) !== null && _a !== void 0 ? _a : cursor.marks();
    // There can be no current marks that exclude the new mark
    return !!newMarkType.isInSet(currentMarks) || !currentMarks.some(function (mark) {
      return mark.type.excludes(newMarkType);
    });
  }
  var ranges = selection.ranges;
  return ranges.some(function (_ref75) {
    var $from = _ref75.$from,
      $to = _ref75.$to;
    var someNodeSupportsMark = $from.depth === 0 ? state.doc.inlineContent && state.doc.type.allowsMarkType(newMarkType) : false;
    state.doc.nodesBetween($from.pos, $to.pos, function (node, _pos, parent) {
      // If we already found a mark that we can enable, return false to bypass the remaining search
      if (someNodeSupportsMark) {
        return false;
      }
      if (node.isInline) {
        var parentAllowsMarkType = !parent || parent.type.allowsMarkType(newMarkType);
        var currentMarksAllowMarkType = !!newMarkType.isInSet(node.marks) || !node.marks.some(function (otherMark) {
          return otherMark.type.excludes(newMarkType);
        });
        someNodeSupportsMark = parentAllowsMarkType && currentMarksAllowMarkType;
      }
      return !someNodeSupportsMark;
    });
    return someNodeSupportsMark;
  });
}
var setMark = function setMark(typeOrName) {
  var attributes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return function (_ref76) {
    var tr = _ref76.tr,
      state = _ref76.state,
      dispatch = _ref76.dispatch;
    var selection = tr.selection;
    var empty = selection.empty,
      ranges = selection.ranges;
    var type = getMarkType(typeOrName, state.schema);
    if (dispatch) {
      if (empty) {
        var oldAttributes = getMarkAttributes(state, type);
        tr.addStoredMark(type.create(_objectSpread2(_objectSpread2({}, oldAttributes), attributes)));
      } else {
        ranges.forEach(function (range) {
          var from = range.$from.pos;
          var to = range.$to.pos;
          state.doc.nodesBetween(from, to, function (node, pos) {
            var trimmedFrom = Math.max(pos, from);
            var trimmedTo = Math.min(pos + node.nodeSize, to);
            var someHasMark = node.marks.find(function (mark) {
              return mark.type === type;
            });
            // if there is already a mark of this type
            // we know that we have to merge its attributes
            // otherwise we add a fresh new mark
            if (someHasMark) {
              node.marks.forEach(function (mark) {
                if (type === mark.type) {
                  tr.addMark(trimmedFrom, trimmedTo, type.create(_objectSpread2(_objectSpread2({}, mark.attrs), attributes)));
                }
              });
            } else {
              tr.addMark(trimmedFrom, trimmedTo, type.create(attributes));
            }
          });
        });
      }
    }
    return canSetMark(state, tr, type);
  };
};
var setMeta = function setMeta(key, value) {
  return function (_ref77) {
    var tr = _ref77.tr;
    tr.setMeta(key, value);
    return true;
  };
};
var setNode = function setNode(typeOrName) {
  var attributes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return function (_ref78) {
    var state = _ref78.state,
      dispatch = _ref78.dispatch,
      chain = _ref78.chain;
    var type = getNodeType(typeOrName, state.schema);
    var attributesToCopy;
    if (state.selection.$anchor.sameParent(state.selection.$head)) {
      // only copy attributes if the selection is pointing to a node of the same type
      attributesToCopy = state.selection.$anchor.parent.attrs;
    }
    // TODO: use a fallback like insertContent?
    if (!type.isTextblock) {
      console.warn('[tiptap warn]: Currently "setNode()" only supports text block nodes.');
      return false;
    }
    return chain()
    // try to convert node to default node if needed
    .command(function (_ref79) {
      var commands = _ref79.commands;
      var canSetBlock = setBlockType(type, _objectSpread2(_objectSpread2({}, attributesToCopy), attributes))(state);
      if (canSetBlock) {
        return true;
      }
      return commands.clearNodes();
    }).command(function (_ref80) {
      var updatedState = _ref80.state;
      return setBlockType(type, _objectSpread2(_objectSpread2({}, attributesToCopy), attributes))(updatedState, dispatch);
    }).run();
  };
};
var setNodeSelection = function setNodeSelection(position) {
  return function (_ref81) {
    var tr = _ref81.tr,
      dispatch = _ref81.dispatch;
    if (dispatch) {
      var doc = tr.doc;
      var from = minMax(position, 0, doc.content.size);
      var selection = NodeSelection.create(doc, from);
      tr.setSelection(selection);
    }
    return true;
  };
};
var setTextSelection = function setTextSelection(position) {
  return function (_ref82) {
    var tr = _ref82.tr,
      dispatch = _ref82.dispatch;
    if (dispatch) {
      var doc = tr.doc;
      var _ref83 = typeof position === 'number' ? {
          from: position,
          to: position
        } : position,
        from = _ref83.from,
        to = _ref83.to;
      var minPos = TextSelection.atStart(doc).from;
      var maxPos = TextSelection.atEnd(doc).to;
      var resolvedFrom = minMax(from, minPos, maxPos);
      var resolvedEnd = minMax(to, minPos, maxPos);
      var selection = TextSelection.create(doc, resolvedFrom, resolvedEnd);
      tr.setSelection(selection);
    }
    return true;
  };
};
var sinkListItem = function sinkListItem(typeOrName) {
  return function (_ref84) {
    var state = _ref84.state,
      dispatch = _ref84.dispatch;
    var type = getNodeType(typeOrName, state.schema);
    return sinkListItem$1(type)(state, dispatch);
  };
};
function ensureMarks(state, splittableMarks) {
  var marks = state.storedMarks || state.selection.$to.parentOffset && state.selection.$from.marks();
  if (marks) {
    var filteredMarks = marks.filter(function (mark) {
      return splittableMarks === null || splittableMarks === void 0 ? void 0 : splittableMarks.includes(mark.type.name);
    });
    state.tr.ensureMarks(filteredMarks);
  }
}
var splitBlock = function splitBlock() {
  var _ref85 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref85$keepMarks = _ref85.keepMarks,
    keepMarks = _ref85$keepMarks === void 0 ? true : _ref85$keepMarks;
  return function (_ref86) {
    var tr = _ref86.tr,
      state = _ref86.state,
      dispatch = _ref86.dispatch,
      editor = _ref86.editor;
    var selection = tr.selection,
      doc = tr.doc;
    var $from = selection.$from,
      $to = selection.$to;
    var extensionAttributes = editor.extensionManager.attributes;
    var newAttributes = getSplittedAttributes(extensionAttributes, $from.node().type.name, $from.node().attrs);
    if (selection instanceof NodeSelection && selection.node.isBlock) {
      if (!$from.parentOffset || !canSplit(doc, $from.pos)) {
        return false;
      }
      if (dispatch) {
        if (keepMarks) {
          ensureMarks(state, editor.extensionManager.splittableMarks);
        }
        tr.split($from.pos).scrollIntoView();
      }
      return true;
    }
    if (!$from.parent.isBlock) {
      return false;
    }
    var atEnd = $to.parentOffset === $to.parent.content.size;
    var deflt = $from.depth === 0 ? undefined : defaultBlockAt($from.node(-1).contentMatchAt($from.indexAfter(-1)));
    var types = atEnd && deflt ? [{
      type: deflt,
      attrs: newAttributes
    }] : undefined;
    var can = canSplit(tr.doc, tr.mapping.map($from.pos), 1, types);
    if (!types && !can && canSplit(tr.doc, tr.mapping.map($from.pos), 1, deflt ? [{
      type: deflt
    }] : undefined)) {
      can = true;
      types = deflt ? [{
        type: deflt,
        attrs: newAttributes
      }] : undefined;
    }
    if (dispatch) {
      if (can) {
        if (selection instanceof TextSelection) {
          tr.deleteSelection();
        }
        tr.split(tr.mapping.map($from.pos), 1, types);
        if (deflt && !atEnd && !$from.parentOffset && $from.parent.type !== deflt) {
          var _first = tr.mapping.map($from.before());
          var $first = tr.doc.resolve(_first);
          if ($from.node(-1).canReplaceWith($first.index(), $first.index() + 1, deflt)) {
            tr.setNodeMarkup(tr.mapping.map($from.before()), deflt);
          }
        }
      }
      if (keepMarks) {
        ensureMarks(state, editor.extensionManager.splittableMarks);
      }
      tr.scrollIntoView();
    }
    return can;
  };
};
var splitListItem = function splitListItem(typeOrName) {
  var overrideAttrs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return function (_ref87) {
    var tr = _ref87.tr,
      state = _ref87.state,
      dispatch = _ref87.dispatch,
      editor = _ref87.editor;
    var _a;
    var type = getNodeType(typeOrName, state.schema);
    var _state$selection7 = state.selection,
      $from = _state$selection7.$from,
      $to = _state$selection7.$to;
    // @ts-ignore
    // eslint-disable-next-line
    var node = state.selection.node;
    if (node && node.isBlock || $from.depth < 2 || !$from.sameParent($to)) {
      return false;
    }
    var grandParent = $from.node(-1);
    if (grandParent.type !== type) {
      return false;
    }
    var extensionAttributes = editor.extensionManager.attributes;
    if ($from.parent.content.size === 0 && $from.node(-1).childCount === $from.indexAfter(-1)) {
      // In an empty block. If this is a nested list, the wrapping
      // list item should be split. Otherwise, bail out and let next
      // command handle lifting.
      if ($from.depth === 2 || $from.node(-3).type !== type || $from.index(-2) !== $from.node(-2).childCount - 1) {
        return false;
      }
      if (dispatch) {
        var wrap = Fragment.empty;
        // eslint-disable-next-line
        var depthBefore = $from.index(-1) ? 1 : $from.index(-2) ? 2 : 3;
        // Build a fragment containing empty versions of the structure
        // from the outer list item to the parent node of the cursor
        for (var d = $from.depth - depthBefore; d >= $from.depth - 3; d -= 1) {
          wrap = Fragment.from($from.node(d).copy(wrap));
        }
        // eslint-disable-next-line
        var depthAfter = $from.indexAfter(-1) < $from.node(-2).childCount ? 1 : $from.indexAfter(-2) < $from.node(-3).childCount ? 2 : 3;
        // Add a second list item with an empty default start node
        var _newNextTypeAttributes = _objectSpread2(_objectSpread2({}, getSplittedAttributes(extensionAttributes, $from.node().type.name, $from.node().attrs)), overrideAttrs);
        var _nextType = ((_a = type.contentMatch.defaultType) === null || _a === void 0 ? void 0 : _a.createAndFill(_newNextTypeAttributes)) || undefined;
        wrap = wrap.append(Fragment.from(type.createAndFill(null, _nextType) || undefined));
        var start = $from.before($from.depth - (depthBefore - 1));
        tr.replace(start, $from.after(-depthAfter), new Slice(wrap, 4 - depthBefore, 0));
        var sel = -1;
        tr.doc.nodesBetween(start, tr.doc.content.size, function (n, pos) {
          if (sel > -1) {
            return false;
          }
          if (n.isTextblock && n.content.size === 0) {
            sel = pos + 1;
          }
        });
        if (sel > -1) {
          tr.setSelection(TextSelection.near(tr.doc.resolve(sel)));
        }
        tr.scrollIntoView();
      }
      return true;
    }
    var nextType = $to.pos === $from.end() ? grandParent.contentMatchAt(0).defaultType : null;
    var newTypeAttributes = _objectSpread2(_objectSpread2({}, getSplittedAttributes(extensionAttributes, grandParent.type.name, grandParent.attrs)), overrideAttrs);
    var newNextTypeAttributes = _objectSpread2(_objectSpread2({}, getSplittedAttributes(extensionAttributes, $from.node().type.name, $from.node().attrs)), overrideAttrs);
    tr["delete"]($from.pos, $to.pos);
    var types = nextType ? [{
      type: type,
      attrs: newTypeAttributes
    }, {
      type: nextType,
      attrs: newNextTypeAttributes
    }] : [{
      type: type,
      attrs: newTypeAttributes
    }];
    if (!canSplit(tr.doc, $from.pos, 2)) {
      return false;
    }
    if (dispatch) {
      var selection = state.selection,
        storedMarks = state.storedMarks;
      var splittableMarks = editor.extensionManager.splittableMarks;
      var marks = storedMarks || selection.$to.parentOffset && selection.$from.marks();
      tr.split($from.pos, 2, types).scrollIntoView();
      if (!marks || !dispatch) {
        return true;
      }
      var filteredMarks = marks.filter(function (mark) {
        return splittableMarks.includes(mark.type.name);
      });
      tr.ensureMarks(filteredMarks);
    }
    return true;
  };
};
var joinListBackwards = function joinListBackwards(tr, listType) {
  var list = findParentNode(function (node) {
    return node.type === listType;
  })(tr.selection);
  if (!list) {
    return true;
  }
  var before = tr.doc.resolve(Math.max(0, list.pos - 1)).before(list.depth);
  if (before === undefined) {
    return true;
  }
  var nodeBefore = tr.doc.nodeAt(before);
  var canJoinBackwards = list.node.type === (nodeBefore === null || nodeBefore === void 0 ? void 0 : nodeBefore.type) && canJoin(tr.doc, list.pos);
  if (!canJoinBackwards) {
    return true;
  }
  tr.join(list.pos);
  return true;
};
var joinListForwards = function joinListForwards(tr, listType) {
  var list = findParentNode(function (node) {
    return node.type === listType;
  })(tr.selection);
  if (!list) {
    return true;
  }
  var after = tr.doc.resolve(list.start).after(list.depth);
  if (after === undefined) {
    return true;
  }
  var nodeAfter = tr.doc.nodeAt(after);
  var canJoinForwards = list.node.type === (nodeAfter === null || nodeAfter === void 0 ? void 0 : nodeAfter.type) && canJoin(tr.doc, after);
  if (!canJoinForwards) {
    return true;
  }
  tr.join(after);
  return true;
};
var toggleList = function toggleList(listTypeOrName, itemTypeOrName, keepMarks) {
  var attributes = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  return function (_ref88) {
    var editor = _ref88.editor,
      tr = _ref88.tr,
      state = _ref88.state,
      dispatch = _ref88.dispatch,
      chain = _ref88.chain,
      commands = _ref88.commands,
      can = _ref88.can;
    var _editor$extensionMana = editor.extensionManager,
      extensions = _editor$extensionMana.extensions,
      splittableMarks = _editor$extensionMana.splittableMarks;
    var listType = getNodeType(listTypeOrName, state.schema);
    var itemType = getNodeType(itemTypeOrName, state.schema);
    var selection = state.selection,
      storedMarks = state.storedMarks;
    var $from = selection.$from,
      $to = selection.$to;
    var range = $from.blockRange($to);
    var marks = storedMarks || selection.$to.parentOffset && selection.$from.marks();
    if (!range) {
      return false;
    }
    var parentList = findParentNode(function (node) {
      return isList(node.type.name, extensions);
    })(selection);
    if (range.depth >= 1 && parentList && range.depth - parentList.depth <= 1) {
      // remove list
      if (parentList.node.type === listType) {
        return commands.liftListItem(itemType);
      }
      // change list type
      if (isList(parentList.node.type.name, extensions) && listType.validContent(parentList.node.content) && dispatch) {
        return chain().command(function () {
          tr.setNodeMarkup(parentList.pos, listType);
          return true;
        }).command(function () {
          return joinListBackwards(tr, listType);
        }).command(function () {
          return joinListForwards(tr, listType);
        }).run();
      }
    }
    if (!keepMarks || !marks || !dispatch) {
      return chain()
      // try to convert node to default node if needed
      .command(function () {
        var canWrapInList = can().wrapInList(listType, attributes);
        if (canWrapInList) {
          return true;
        }
        return commands.clearNodes();
      }).wrapInList(listType, attributes).command(function () {
        return joinListBackwards(tr, listType);
      }).command(function () {
        return joinListForwards(tr, listType);
      }).run();
    }
    return chain()
    // try to convert node to default node if needed
    .command(function () {
      var canWrapInList = can().wrapInList(listType, attributes);
      var filteredMarks = marks.filter(function (mark) {
        return splittableMarks.includes(mark.type.name);
      });
      tr.ensureMarks(filteredMarks);
      if (canWrapInList) {
        return true;
      }
      return commands.clearNodes();
    }).wrapInList(listType, attributes).command(function () {
      return joinListBackwards(tr, listType);
    }).command(function () {
      return joinListForwards(tr, listType);
    }).run();
  };
};
var toggleMark = function toggleMark(typeOrName) {
  var attributes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return function (_ref89) {
    var state = _ref89.state,
      commands = _ref89.commands;
    var _options$extendEmptyM = options.extendEmptyMarkRange,
      extendEmptyMarkRange = _options$extendEmptyM === void 0 ? false : _options$extendEmptyM;
    var type = getMarkType(typeOrName, state.schema);
    var isActive = isMarkActive(state, type, attributes);
    if (isActive) {
      return commands.unsetMark(type, {
        extendEmptyMarkRange: extendEmptyMarkRange
      });
    }
    return commands.setMark(type, attributes);
  };
};
var toggleNode = function toggleNode(typeOrName, toggleTypeOrName) {
  var attributes = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return function (_ref90) {
    var state = _ref90.state,
      commands = _ref90.commands;
    var type = getNodeType(typeOrName, state.schema);
    var toggleType = getNodeType(toggleTypeOrName, state.schema);
    var isActive = isNodeActive(state, type, attributes);
    var attributesToCopy;
    if (state.selection.$anchor.sameParent(state.selection.$head)) {
      // only copy attributes if the selection is pointing to a node of the same type
      attributesToCopy = state.selection.$anchor.parent.attrs;
    }
    if (isActive) {
      return commands.setNode(toggleType, attributesToCopy);
    }
    // If the node is not active, we want to set the new node type with the given attributes
    // Copying over the attributes from the current node if the selection is pointing to a node of the same type
    return commands.setNode(type, _objectSpread2(_objectSpread2({}, attributesToCopy), attributes));
  };
};
var toggleWrap = function toggleWrap(typeOrName) {
  var attributes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return function (_ref91) {
    var state = _ref91.state,
      commands = _ref91.commands;
    var type = getNodeType(typeOrName, state.schema);
    var isActive = isNodeActive(state, type, attributes);
    if (isActive) {
      return commands.lift(type);
    }
    return commands.wrapIn(type, attributes);
  };
};
var undoInputRule = function undoInputRule() {
  return function (_ref92) {
    var state = _ref92.state,
      dispatch = _ref92.dispatch;
    var plugins = state.plugins;
    for (var i = 0; i < plugins.length; i += 1) {
      var plugin = plugins[i];
      var undoable = void 0;
      // @ts-ignore
      // eslint-disable-next-line
      if (plugin.spec.isInputRules && (undoable = plugin.getState(state))) {
        if (dispatch) {
          var tr = state.tr;
          var toUndo = undoable.transform;
          for (var j = toUndo.steps.length - 1; j >= 0; j -= 1) {
            tr.step(toUndo.steps[j].invert(toUndo.docs[j]));
          }
          if (undoable.text) {
            var marks = tr.doc.resolve(undoable.from).marks();
            tr.replaceWith(undoable.from, undoable.to, state.schema.text(undoable.text, marks));
          } else {
            tr["delete"](undoable.from, undoable.to);
          }
        }
        return true;
      }
    }
    return false;
  };
};
var unsetAllMarks = function unsetAllMarks() {
  return function (_ref93) {
    var tr = _ref93.tr,
      dispatch = _ref93.dispatch;
    var selection = tr.selection;
    var empty = selection.empty,
      ranges = selection.ranges;
    if (empty) {
      return true;
    }
    if (dispatch) {
      ranges.forEach(function (range) {
        tr.removeMark(range.$from.pos, range.$to.pos);
      });
    }
    return true;
  };
};
var unsetMark = function unsetMark(typeOrName) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return function (_ref94) {
    var tr = _ref94.tr,
      state = _ref94.state,
      dispatch = _ref94.dispatch;
    var _a;
    var _options$extendEmptyM2 = options.extendEmptyMarkRange,
      extendEmptyMarkRange = _options$extendEmptyM2 === void 0 ? false : _options$extendEmptyM2;
    var selection = tr.selection;
    var type = getMarkType(typeOrName, state.schema);
    var $from = selection.$from,
      empty = selection.empty,
      ranges = selection.ranges;
    if (!dispatch) {
      return true;
    }
    if (empty && extendEmptyMarkRange) {
      var from = selection.from,
        to = selection.to;
      var attrs = (_a = $from.marks().find(function (mark) {
        return mark.type === type;
      })) === null || _a === void 0 ? void 0 : _a.attrs;
      var range = getMarkRange($from, type, attrs);
      if (range) {
        from = range.from;
        to = range.to;
      }
      tr.removeMark(from, to, type);
    } else {
      ranges.forEach(function (range) {
        tr.removeMark(range.$from.pos, range.$to.pos, type);
      });
    }
    tr.removeStoredMark(type);
    return true;
  };
};
var updateAttributes = function updateAttributes(typeOrName) {
  var attributes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return function (_ref95) {
    var tr = _ref95.tr,
      state = _ref95.state,
      dispatch = _ref95.dispatch;
    var nodeType = null;
    var markType = null;
    var schemaType = getSchemaTypeNameByName(typeof typeOrName === 'string' ? typeOrName : typeOrName.name, state.schema);
    if (!schemaType) {
      return false;
    }
    if (schemaType === 'node') {
      nodeType = getNodeType(typeOrName, state.schema);
    }
    if (schemaType === 'mark') {
      markType = getMarkType(typeOrName, state.schema);
    }
    if (dispatch) {
      tr.selection.ranges.forEach(function (range) {
        var from = range.$from.pos;
        var to = range.$to.pos;
        var lastPos;
        var lastNode;
        var trimmedFrom;
        var trimmedTo;
        if (tr.selection.empty) {
          state.doc.nodesBetween(from, to, function (node, pos) {
            if (nodeType && nodeType === node.type) {
              trimmedFrom = Math.max(pos, from);
              trimmedTo = Math.min(pos + node.nodeSize, to);
              lastPos = pos;
              lastNode = node;
            }
          });
        } else {
          state.doc.nodesBetween(from, to, function (node, pos) {
            if (pos < from && nodeType && nodeType === node.type) {
              trimmedFrom = Math.max(pos, from);
              trimmedTo = Math.min(pos + node.nodeSize, to);
              lastPos = pos;
              lastNode = node;
            }
            if (pos >= from && pos <= to) {
              if (nodeType && nodeType === node.type) {
                tr.setNodeMarkup(pos, undefined, _objectSpread2(_objectSpread2({}, node.attrs), attributes));
              }
              if (markType && node.marks.length) {
                node.marks.forEach(function (mark) {
                  if (markType === mark.type) {
                    var trimmedFrom2 = Math.max(pos, from);
                    var trimmedTo2 = Math.min(pos + node.nodeSize, to);
                    tr.addMark(trimmedFrom2, trimmedTo2, markType.create(_objectSpread2(_objectSpread2({}, mark.attrs), attributes)));
                  }
                });
              }
            }
          });
        }
        if (lastNode) {
          if (lastPos !== undefined) {
            tr.setNodeMarkup(lastPos, undefined, _objectSpread2(_objectSpread2({}, lastNode.attrs), attributes));
          }
          if (markType && lastNode.marks.length) {
            lastNode.marks.forEach(function (mark) {
              if (markType === mark.type) {
                tr.addMark(trimmedFrom, trimmedTo, markType.create(_objectSpread2(_objectSpread2({}, mark.attrs), attributes)));
              }
            });
          }
        }
      });
    }
    return true;
  };
};
var wrapIn = function wrapIn(typeOrName) {
  var attributes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return function (_ref96) {
    var state = _ref96.state,
      dispatch = _ref96.dispatch;
    var type = getNodeType(typeOrName, state.schema);
    return wrapIn$1(type, attributes)(state, dispatch);
  };
};
var wrapInList = function wrapInList(typeOrName) {
  var attributes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return function (_ref97) {
    var state = _ref97.state,
      dispatch = _ref97.dispatch;
    var type = getNodeType(typeOrName, state.schema);
    return wrapInList$1(type, attributes)(state, dispatch);
  };
};
var commands = /*#__PURE__*/Object.freeze({
  __proto__: null,
  blur: blur,
  clearContent: clearContent,
  clearNodes: clearNodes,
  command: command,
  createParagraphNear: createParagraphNear,
  cut: cut,
  deleteCurrentNode: deleteCurrentNode,
  deleteNode: deleteNode,
  deleteRange: deleteRange,
  deleteSelection: deleteSelection,
  enter: enter,
  exitCode: exitCode,
  extendMarkRange: extendMarkRange,
  first: first,
  focus: focus,
  forEach: forEach,
  insertContent: insertContent,
  insertContentAt: insertContentAt,
  joinBackward: joinBackward,
  joinDown: joinDown,
  joinForward: joinForward,
  joinItemBackward: joinItemBackward,
  joinItemForward: joinItemForward,
  joinTextblockBackward: joinTextblockBackward,
  joinTextblockForward: joinTextblockForward,
  joinUp: joinUp,
  keyboardShortcut: keyboardShortcut,
  lift: lift,
  liftEmptyBlock: liftEmptyBlock,
  liftListItem: liftListItem,
  newlineInCode: newlineInCode,
  resetAttributes: resetAttributes,
  scrollIntoView: scrollIntoView,
  selectAll: selectAll,
  selectNodeBackward: selectNodeBackward,
  selectNodeForward: selectNodeForward,
  selectParentNode: selectParentNode,
  selectTextblockEnd: selectTextblockEnd,
  selectTextblockStart: selectTextblockStart,
  setContent: setContent,
  setMark: setMark,
  setMeta: setMeta,
  setNode: setNode,
  setNodeSelection: setNodeSelection,
  setTextSelection: setTextSelection,
  sinkListItem: sinkListItem,
  splitBlock: splitBlock,
  splitListItem: splitListItem,
  toggleList: toggleList,
  toggleMark: toggleMark,
  toggleNode: toggleNode,
  toggleWrap: toggleWrap,
  undoInputRule: undoInputRule,
  unsetAllMarks: unsetAllMarks,
  unsetMark: unsetMark,
  updateAttributes: updateAttributes,
  wrapIn: wrapIn,
  wrapInList: wrapInList
});
var Commands = Extension.create({
  name: 'commands',
  addCommands: function addCommands() {
    return _objectSpread2({}, commands);
  }
});
var Drop = Extension.create({
  name: 'drop',
  addProseMirrorPlugins: function addProseMirrorPlugins() {
    var _this17 = this;
    return [new Plugin({
      key: new PluginKey('tiptapDrop'),
      props: {
        handleDrop: function handleDrop(_, e, slice, moved) {
          _this17.editor.emit('drop', {
            editor: _this17.editor,
            event: e,
            slice: slice,
            moved: moved
          });
        }
      }
    })];
  }
});
var Editable = Extension.create({
  name: 'editable',
  addProseMirrorPlugins: function addProseMirrorPlugins() {
    var _this18 = this;
    return [new Plugin({
      key: new PluginKey('editable'),
      props: {
        editable: function editable() {
          return _this18.editor.options.editable;
        }
      }
    })];
  }
});
var FocusEvents = Extension.create({
  name: 'focusEvents',
  addProseMirrorPlugins: function addProseMirrorPlugins() {
    var editor = this.editor;
    return [new Plugin({
      key: new PluginKey('focusEvents'),
      props: {
        handleDOMEvents: {
          focus: function focus(view, event) {
            editor.isFocused = true;
            var transaction = editor.state.tr.setMeta('focus', {
              event: event
            }).setMeta('addToHistory', false);
            view.dispatch(transaction);
            return false;
          },
          blur: function blur(view, event) {
            editor.isFocused = false;
            var transaction = editor.state.tr.setMeta('blur', {
              event: event
            }).setMeta('addToHistory', false);
            view.dispatch(transaction);
            return false;
          }
        }
      }
    })];
  }
});
var Keymap = Extension.create({
  name: 'keymap',
  addKeyboardShortcuts: function addKeyboardShortcuts() {
    var _this19 = this;
    var handleBackspace = function handleBackspace() {
      return _this19.editor.commands.first(function (_ref98) {
        var commands = _ref98.commands;
        return [function () {
          return commands.undoInputRule();
        },
        // maybe convert first text block node to default node
        function () {
          return commands.command(function (_ref99) {
            var tr = _ref99.tr;
            var selection = tr.selection,
              doc = tr.doc;
            var empty = selection.empty,
              $anchor = selection.$anchor;
            var pos = $anchor.pos,
              parent = $anchor.parent;
            var $parentPos = $anchor.parent.isTextblock && pos > 0 ? tr.doc.resolve(pos - 1) : $anchor;
            var parentIsIsolating = $parentPos.parent.type.spec.isolating;
            var parentPos = $anchor.pos - $anchor.parentOffset;
            var isAtStart = parentIsIsolating && $parentPos.parent.childCount === 1 ? parentPos === $anchor.pos : Selection.atStart(doc).from === pos;
            if (!empty || !parent.type.isTextblock || parent.textContent.length || !isAtStart || isAtStart && $anchor.parent.type.name === 'paragraph' // prevent clearNodes when no nodes to clear, otherwise history stack is appended
            ) {
              return false;
            }
            return commands.clearNodes();
          });
        }, function () {
          return commands.deleteSelection();
        }, function () {
          return commands.joinBackward();
        }, function () {
          return commands.selectNodeBackward();
        }];
      });
    };
    var handleDelete = function handleDelete() {
      return _this19.editor.commands.first(function (_ref100) {
        var commands = _ref100.commands;
        return [function () {
          return commands.deleteSelection();
        }, function () {
          return commands.deleteCurrentNode();
        }, function () {
          return commands.joinForward();
        }, function () {
          return commands.selectNodeForward();
        }];
      });
    };
    var handleEnter = function handleEnter() {
      return _this19.editor.commands.first(function (_ref101) {
        var commands = _ref101.commands;
        return [function () {
          return commands.newlineInCode();
        }, function () {
          return commands.createParagraphNear();
        }, function () {
          return commands.liftEmptyBlock();
        }, function () {
          return commands.splitBlock();
        }];
      });
    };
    var baseKeymap = {
      Enter: handleEnter,
      'Mod-Enter': function ModEnter() {
        return _this19.editor.commands.exitCode();
      },
      Backspace: handleBackspace,
      'Mod-Backspace': handleBackspace,
      'Shift-Backspace': handleBackspace,
      Delete: handleDelete,
      'Mod-Delete': handleDelete,
      'Mod-a': function ModA() {
        return _this19.editor.commands.selectAll();
      }
    };
    var pcKeymap = _objectSpread2({}, baseKeymap);
    var macKeymap = _objectSpread2(_objectSpread2({}, baseKeymap), {}, {
      'Ctrl-h': handleBackspace,
      'Alt-Backspace': handleBackspace,
      'Ctrl-d': handleDelete,
      'Ctrl-Alt-Backspace': handleDelete,
      'Alt-Delete': handleDelete,
      'Alt-d': handleDelete,
      'Ctrl-a': function CtrlA() {
        return _this19.editor.commands.selectTextblockStart();
      },
      'Ctrl-e': function CtrlE() {
        return _this19.editor.commands.selectTextblockEnd();
      }
    });
    if (isiOS() || isMacOS()) {
      return macKeymap;
    }
    return pcKeymap;
  },
  addProseMirrorPlugins: function addProseMirrorPlugins() {
    var _this20 = this;
    return [
    // With this plugin we check if the whole document was selected and deleted.
    // In this case we will additionally call `clearNodes()` to convert e.g. a heading
    // to a paragraph if necessary.
    // This is an alternative to ProseMirror's `AllSelection`, which doesn’t work well
    // with many other commands.
    new Plugin({
      key: new PluginKey('clearDocument'),
      appendTransaction: function appendTransaction(transactions, oldState, newState) {
        if (transactions.some(function (tr) {
          return tr.getMeta('composition');
        })) {
          return;
        }
        var docChanges = transactions.some(function (transaction) {
          return transaction.docChanged;
        }) && !oldState.doc.eq(newState.doc);
        var ignoreTr = transactions.some(function (transaction) {
          return transaction.getMeta('preventClearDocument');
        });
        if (!docChanges || ignoreTr) {
          return;
        }
        var _oldState$selection = oldState.selection,
          empty = _oldState$selection.empty,
          from = _oldState$selection.from,
          to = _oldState$selection.to;
        var allFrom = Selection.atStart(oldState.doc).from;
        var allEnd = Selection.atEnd(oldState.doc).to;
        var allWasSelected = from === allFrom && to === allEnd;
        if (empty || !allWasSelected) {
          return;
        }
        var isEmpty = isNodeEmpty(newState.doc);
        if (!isEmpty) {
          return;
        }
        var tr = newState.tr;
        var state = createChainableState({
          state: newState,
          transaction: tr
        });
        var _CommandManager3 = new CommandManager({
            editor: _this20.editor,
            state: state
          }),
          commands = _CommandManager3.commands;
        commands.clearNodes();
        if (!tr.steps.length) {
          return;
        }
        return tr;
      }
    })];
  }
});
var Paste = Extension.create({
  name: 'paste',
  addProseMirrorPlugins: function addProseMirrorPlugins() {
    var _this21 = this;
    return [new Plugin({
      key: new PluginKey('tiptapPaste'),
      props: {
        handlePaste: function handlePaste(_view, e, slice) {
          _this21.editor.emit('paste', {
            editor: _this21.editor,
            event: e,
            slice: slice
          });
        }
      }
    })];
  }
});
var Tabindex = Extension.create({
  name: 'tabindex',
  addProseMirrorPlugins: function addProseMirrorPlugins() {
    var _this22 = this;
    return [new Plugin({
      key: new PluginKey('tabindex'),
      props: {
        attributes: function attributes() {
          return _this22.editor.isEditable ? {
            tabindex: '0'
          } : {};
        }
      }
    })];
  }
});

/**
 * The Node class is used to create custom node extensions.
 * @see https://tiptap.dev/api/extensions#create-a-new-extension
 */
var Node = /*#__PURE__*/function () {
  function Node() {
    var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    _classCallCheck(this, Node);
    this.type = 'node';
    this.name = 'node';
    this.parent = null;
    this.child = null;
    this.config = {
      name: this.name,
      defaultOptions: {}
    };
    this.config = _objectSpread2(_objectSpread2({}, this.config), config);
    this.name = this.config.name;
    if (config.defaultOptions && Object.keys(config.defaultOptions).length > 0) {
      console.warn("[tiptap warn]: BREAKING CHANGE: \"defaultOptions\" is deprecated. Please use \"addOptions\" instead. Found in extension: \"".concat(this.name, "\"."));
    }
    // TODO: remove `addOptions` fallback
    this.options = this.config.defaultOptions;
    if (this.config.addOptions) {
      this.options = callOrReturn(getExtensionField(this, 'addOptions', {
        name: this.name
      }));
    }
    this.storage = callOrReturn(getExtensionField(this, 'addStorage', {
      name: this.name,
      options: this.options
    })) || {};
  }
  return _createClass(Node, [{
    key: "configure",
    value: function configure() {
      var _this28 = this;
      var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      // return a new instance so we can use the same extension
      // with different calls of `configure`
      var extension = this.extend(_objectSpread2(_objectSpread2({}, this.config), {}, {
        addOptions: function addOptions() {
          return mergeDeep(_this28.options, options);
        }
      }));
      // Always preserve the current name
      extension.name = this.name;
      // Set the parent to be our parent
      extension.parent = this.parent;
      return extension;
    }
  }, {
    key: "extend",
    value: function extend() {
      var extendedConfig = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      var extension = new Node(extendedConfig);
      extension.parent = this;
      this.child = extension;
      extension.name = extendedConfig.name ? extendedConfig.name : extension.parent.name;
      if (extendedConfig.defaultOptions && Object.keys(extendedConfig.defaultOptions).length > 0) {
        console.warn("[tiptap warn]: BREAKING CHANGE: \"defaultOptions\" is deprecated. Please use \"addOptions\" instead. Found in extension: \"".concat(extension.name, "\"."));
      }
      extension.options = callOrReturn(getExtensionField(extension, 'addOptions', {
        name: extension.name
      }));
      extension.storage = callOrReturn(getExtensionField(extension, 'addStorage', {
        name: extension.name,
        options: extension.options
      }));
      return extension;
    }
  }], [{
    key: "create",
    value: function create() {
      var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return new Node(config);
    }
  }]);
}();

export { CommandManager, Extension, Node, callOrReturn, createChainableState, createDocument, createNodeFromContent, defaultBlockAt, deleteProps, elementFromString, findParentNode, findParentNodeClosestToPos, getExtensionField, getMarkAttributes, getMarkRange, getMarkType, getNodeType, getSchemaTypeNameByName, getSplittedAttributes, getTextBetween, getTextSerializersFromSchema, isFunction, isList, isMacOS, isMarkActive, isNodeActive, isNodeEmpty, isPlainObject, isRegExp, isTextSelection, isiOS, mergeAttributes, mergeDeep, minMax, objectIncludes, resolveFocusPosition, selectionToInsertionEnd, splitExtensions };
